import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { usePWAInstall } from "../hooks/usePWAInstall";
import "./LandingPage.css";

const SECTION_IDS = {
  inicio: "inicio",
  tecnologia: "tecnologia-ra",
  marcadores: "marcadores",
  como: "como-funciona",
  explora: "explora",
  instalar: "instalar",
};

function LavaParticles() {
  const particles = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => ({
        id: i,
        left: `${(i * 3.7 + (i % 7) * 2) % 100}%`,
        duration: `${7 + (i % 5) * 1.2}s`,
        delay: `${(i % 12) * 0.35}s`,
        size: `${2 + (i % 3)}px`,
      })),
    []
  );
  return (
    <div className="landing-lava" aria-hidden>
      {particles.map((p) => (
        <span
          key={p.id}
          className="landing-lava-particle"
          style={{
            left: p.left,
            animationDuration: p.duration,
            animationDelay: p.delay,
            width: p.size,
            height: p.size,
          }}
        />
      ))}
    </div>
  );
}

function MarkerSlot({ n }) {
  const base = process.env.PUBLIC_URL || "";
  const [broken, setBroken] = useState(false);
  const src = `${base}/markers/marker-${n}.png`;

  return (
    <figure className="landing-marker">
      {!broken && (
        <img
          src={src}
          alt=""
          className="landing-marker-img"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      )}
      {broken && (
        <div className="landing-marker-placeholder" aria-hidden>
          <span className="landing-marker-ph-num">{n}</span>
          <span className="landing-marker-ph-text">Demo</span>
        </div>
      )}
      <span className="landing-sr-only">Marcador {n}</span>
    </figure>
  );
}

export default function LandingPage() {
  const { canInstall, install, isMobile } = usePWAInstall();

  return (
    <div className="landing">
      <LavaParticles />
      <nav className="landing-nav" aria-label="Principal">
        <div className="landing-nav-inner">
          <a href={`#${SECTION_IDS.inicio}`} className="landing-brand" aria-label="Ckelar Volcanes">
            <img
              src={`${process.env.PUBLIC_URL || ""}/icons/Ckelar_logo4.svg`}
              alt="Ckelar Volcanes"
              className="landing-brand-logo"
              loading="eager"
            />
          </a>
          <div className="landing-nav-actions">
            <Link
              to="/ar"
              state={{ skipIntro: true }}
              className="landing-nav-explore"
            >
              Inicia experiencia RA
            </Link>
          </div>
        </div>
      </nav>

      <header id={SECTION_IDS.inicio} className="landing-hero">
        <div className="landing-hero-inner">
          <p className="landing-hero-kicker">Una experiencia educativa de Ckelar volcanes.</p>
          <h1 className="landing-sr-only">Vigilante de volcanes</h1>
          <p className="landing-sr-only">Explorando los gigantes activos de Chile</p>
          <div className="landing-hero-title-image-wrap" aria-hidden>
            <img
              src={`${process.env.PUBLIC_URL || ""}/Titulo.png`}
              alt=""
              className="landing-hero-title-image"
              loading="eager"
            />
          </div>

          <section
            id={SECTION_IDS.como}
            className="landing-hero-how"
            aria-labelledby="hero-how-title"
          >
            <h2 id="hero-how-title" className="landing-hero-how-title">
              Cómo funciona
            </h2>
            <ol className="landing-hero-timeline">
              <li className="landing-hero-timeline-item">
                <span className="landing-hero-step-index" aria-hidden="true">
                  1
                </span>
                <span className="landing-hero-step-text">
                  Inicia la experiencia RA
                </span>
              </li>
              <li className="landing-hero-timeline-item">
                <span className="landing-hero-step-index" aria-hidden="true">
                  2
                </span>
                <span className="landing-hero-step-text">
                  Enfoca el marcador del libro
                </span>
              </li>
              <li className="landing-hero-timeline-item">
                <span className="landing-hero-step-index" aria-hidden="true">
                  3
                </span>
                <span className="landing-hero-step-text">
                  Explora el modelo en tu espacio
                </span>
              </li>
            </ol>
          </section>

          <div className="landing-hero-ctas">
            <Link
              to="/ar"
              state={{ skipIntro: true }}
              className="landing-btn landing-btn-primary"
            >
              <span className="landing-btn-icon" aria-hidden>
                ◈
              </span>
              Inicia la experiencia RA
            </Link>
          </div>
        </div>
      </header>

      <section
        id={SECTION_IDS.marcadores}
        className="landing-section"
        aria-labelledby="gal-title"
      >
        <div className="landing-section-inner">
          <h2 id="gal-title" className="landing-section-title">
            Marcadores del libro
          </h2>
          <div className="landing-marker-intro">
            <p className="landing-section-lead landing-section-lead-tight">
              Seis tarjetas para explorar en RA.
            </p>
            <p className="landing-marker-sub">Búscalas en el libro.</p>
          </div>
          <div className="landing-marker-grid">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <MarkerSlot key={n} n={n} />
            ))}
          </div>

        </div>
      </section>

      <section
        id={SECTION_IDS.tecnologia}
        className="landing-section landing-tech"
        aria-labelledby="tech-title"
      >
        <div className="landing-section-inner">
          <h2 id="tech-title" className="landing-section-title landing-tech-title">
            <span className="landing-tech-title-w">Realidad </span>
            <span className="landing-tech-title-c">Aumentada</span>
          </h2>
          <p className="landing-section-lead">
            Inicia la experiencia RA, enfoca una tarjeta y explora el modelo en
            tu entorno.
          </p>
          <div className="landing-phone-mock">
            <div className="landing-phone-frame">
              <div className="landing-phone-screen">
                <div className="landing-phone-grid" />
                <div className="landing-phone-hud-corner tl" />
                <div className="landing-phone-hud-corner tr" />
                <div className="landing-phone-hud-corner bl" />
                <div className="landing-phone-hud-corner br" />
                <div className="landing-phone-volcano-icon" aria-hidden />
                <p className="landing-phone-hud-label">Volcán 1 — 3D RA</p>
              </div>
            </div>
          </div>
          <ul className="landing-tech-cards">
            <li className="glass-card">
              <strong>Proyección en el libro</strong>
              <span>Los marcadores del libro activan el modelo en RA.</span>
            </li>
            <li className="glass-card">
              <strong>Animaciones</strong>
              <span>Erupciones y procesos didácticos en el modelo.</span>
            </li>
            <li className="glass-card">
              <strong>Capas geológicas</strong>
              <span>Navegación por estructura interna del volcán.</span>
            </li>
            <li className="glass-card">
              <strong>Datos en tiempo real</strong>
              <span>Superposición de información mientras exploras.</span>
            </li>
          </ul>
        </div>
      </section>

      <section
        id={SECTION_IDS.explora}
        className="landing-section landing-cta"
        aria-labelledby="cta-title"
      >
        <div className="landing-section-inner">
          <div className="landing-cta-panel glass-card">
            <div className="landing-cta-icon-wrap" aria-hidden>
              <span className="landing-cta-volcano-emoji">🌋</span>
            </div>
            <h2 id="cta-title" className="landing-cta-title">
              <span className="landing-cta-title-w">Instalar en el </span>
              <span className="landing-cta-title-o">móvil</span>
            </h2>
            <p className="landing-cta-text">
              Instala la app en tu móvil y abre la experiencia en un toque.
            </p>

            <div id={SECTION_IDS.instalar} className="landing-install-box">
              <h3 className="landing-install-title">Guardar en la pantalla de inicio</h3>
              <p className="landing-install-text">
                Añade Ckelar Volcanes a tu pantalla de inicio.
              </p>
              <button
                type="button"
                className="landing-btn landing-btn-install"
                onClick={() => install()}
                disabled={!isMobile || !canInstall}
                aria-disabled={!isMobile || !canInstall}
                title={!isMobile || !canInstall ? "Disponible al abrir desde un móvil compatible" : undefined}
              >
                Instalar app
              </button>
              {isMobile && !canInstall && (
                <p className="landing-install-hint">
                  En iPhone: Compartir → Añadir a pantalla de inicio. En
                  Android, el navegador mostrará la opción de instalar cuando
                  esté disponible.
                </p>
              )}
              {!isMobile && (
                <p className="landing-install-hint">
                  La instalación como app está pensada desde el móvil. Abre este
                  sitio en tu teléfono para instalar o iniciar la experiencia RA.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <p>Ckelar volcanes</p>
      </footer>
    </div>
  );
}
